import { Vector2 } from "three";

/**
 * 2D tunnel cross-section profile definitions.
 *
 * Each profile is a closed polyline of Vector2 points in a local coordinate system:
 *   - X = horizontal (positive = right when looking along tunnel direction)
 *   - Y = vertical   (positive = up)
 *   - Origin = centerline point (road center at road surface level)
 *
 * Points are ordered clockwise starting from bottom-left.
 */

export interface TunnelProfileType {
  name: string;
  /** Closed polyline (first and last point should match for clean rendering) */
  points: Vector2[];
  /** Color for rendering */
  color: string;
}

/**
 * Generate a road tunnel arch profile.
 * Flat road surface, vertical walls up to spring line, semicircular arch above.
 *
 * @param roadWidth   Total road width (m)
 * @param wallHeight  Height of vertical walls from road surface to arch spring (m)
 * @param archHeight  Height of arch above spring line (m)
 * @param archSegments Number of segments for the arch curve
 */
function makeRoadTunnelProfile(
  roadWidth: number,
  wallHeight: number,
  archHeight: number,
  archSegments: number = 24,
): Vector2[] {
  const hw = roadWidth / 2; // half-width
  const points: Vector2[] = [];

  // Bottom-left corner (road surface)
  points.push(new Vector2(-hw, 0));

  // Left wall going up
  points.push(new Vector2(-hw, wallHeight));

  // Arch from left to right (semi-elliptical)
  // Parametric: center at (0, wallHeight), semi-axes (hw, archHeight)
  for (let i = 0; i <= archSegments; i++) {
    const angle = Math.PI - (i / archSegments) * Math.PI; // PI to 0
    const x = hw * Math.cos(angle);
    const y = wallHeight + archHeight * Math.sin(angle);
    points.push(new Vector2(x, y));
  }

  // Right wall going down
  points.push(new Vector2(hw, wallHeight));
  points.push(new Vector2(hw, 0));

  // Close the profile (road surface back to start)
  points.push(new Vector2(-hw, 0));

  return points;
}

/** Standard Norwegian T9.5 profile: 2-lane road tunnel (~9.5m road width) */
export const PROFILE_2_LANE: TunnelProfileType = {
  name: "T9.5 (2-lane)",
  points: makeRoadTunnelProfile(9.5, 1.0, 5.25, 24),
  color: "#00ff88",
};

/** Wider T12.5 profile: 3-lane road tunnel (~12.5m road width) */
export const PROFILE_3_LANE: TunnelProfileType = {
  name: "T12.5 (3-lane)",
  points: makeRoadTunnelProfile(12.5, 1.5, 6.5, 24),
  color: "#ff8800",
};

/** Large tunnel profile: ~14.9m wide, ~11.1m tall (from scan pointcloud, scaled 95%) */
export const PROFILE_LARGE: TunnelProfileType = {
  name: "Large (15m)",
  points: makeRoadTunnelProfile(15.7 * 0.95, 2.0 * 0.95, 9.7 * 0.95, 32),
  color: "#44aaff",
};

/** Registry of all available profile types */
export const PROFILE_TYPES: Record<string, TunnelProfileType> = {
  "2-lane": PROFILE_2_LANE,
  "3-lane": PROFILE_3_LANE,
  "large": PROFILE_LARGE,
};

/**
 * A profile assignment: which profile type to use at which station range.
 */
export interface ProfileAssignment {
  /** Start station in meters */
  fromStation: number;
  /** End station in meters */
  toStation: number;
  /** Profile type key from PROFILE_TYPES */
  profileType: string;
  /** Optional [x, y] offset applied to all profile points (meters). x=right, y=up. */
  offset?: [number, number];
}

/**
 * Look up which profile type applies at a given station.
 * Supports smooth blending across transition zones where adjacent
 * assignments overlap or meet. A transition zone of `transitionLength`
 * meters is applied around each boundary.
 */
export function getProfileAtStation(
  station: number,
  assignments: ProfileAssignment[],
  defaultProfile: string = "2-lane",
  transitionLength: number = 20,
): TunnelProfileType {
  // Find the assignment that contains this station
  let current: TunnelProfileType | null = null;
  let currentAssignment: ProfileAssignment | null = null;
  for (const a of assignments) {
    if (station >= a.fromStation && station <= a.toStation) {
      current = PROFILE_TYPES[a.profileType] ?? PROFILE_TYPES[defaultProfile];
      currentAssignment = a;
      break;
    }
  }
  if (!current) current = PROFILE_TYPES[defaultProfile];
  if (!currentAssignment) return current;

  const halfT = transitionLength / 2;

  // Check if we're near the start boundary — blend with previous assignment
  if (station < currentAssignment.fromStation + halfT) {
    const prev = findAssignmentBefore(currentAssignment.fromStation, assignments);
    if (prev) {
      const prevProfile = applyOffset(
        PROFILE_TYPES[prev.profileType] ?? PROFILE_TYPES[defaultProfile], prev.offset);
      const currProfile = applyOffset(current, currentAssignment.offset);
      if (prevProfile.points.length === currProfile.points.length) {
        const boundary = currentAssignment.fromStation;
        const t = Math.max(0, Math.min(1, (station - (boundary - halfT)) / transitionLength));
        return blendProfiles(prevProfile, currProfile, smoothstep(t));
      }
    }
  }

  // Check if we're near the end boundary — blend with next assignment
  if (station > currentAssignment.toStation - halfT) {
    const next = findAssignmentAfter(currentAssignment.toStation, assignments);
    if (next) {
      const nextProfile = applyOffset(
        PROFILE_TYPES[next.profileType] ?? PROFILE_TYPES[defaultProfile], next.offset);
      const currProfile = applyOffset(current, currentAssignment.offset);
      if (nextProfile.points.length === currProfile.points.length) {
        const boundary = currentAssignment.toStation;
        const t = Math.max(0, Math.min(1, (station - (boundary - halfT)) / transitionLength));
        return blendProfiles(currProfile, nextProfile, smoothstep(t));
      }
    }
  }

  return applyOffset(current, currentAssignment.offset);
}

/** Apply an optional [x, y] offset to all profile points */
function applyOffset(profile: TunnelProfileType, offset?: [number, number]): TunnelProfileType {
  if (!offset || (offset[0] === 0 && offset[1] === 0)) return profile;
  return {
    ...profile,
    points: profile.points.map(p => new Vector2(p.x + offset[0], p.y + offset[1])),
  };
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function findAssignmentBefore(station: number, assignments: ProfileAssignment[]): ProfileAssignment | null {
  let best: ProfileAssignment | null = null;
  for (const a of assignments) {
    if (a.toStation <= station && (!best || a.toStation > best.toStation)) {
      best = a;
    }
  }
  return best;
}

function findAssignmentAfter(station: number, assignments: ProfileAssignment[]): ProfileAssignment | null {
  let best: ProfileAssignment | null = null;
  for (const a of assignments) {
    if (a.fromStation >= station && (!best || a.fromStation < best.fromStation)) {
      best = a;
    }
  }
  return best;
}

function blendProfiles(a: TunnelProfileType, b: TunnelProfileType, t: number): TunnelProfileType {
  const points = a.points.map((pa, i) => {
    const pb = b.points[i];
    return new Vector2(
      pa.x + (pb.x - pa.x) * t,
      pa.y + (pb.y - pa.y) * t,
    );
  });
  return {
    name: `${a.name} → ${b.name}`,
    points,
    color: t < 0.5 ? a.color : b.color,
  };
}
